import AppLink from "@/integrations/next/components/AppLink"
import AnimateOnEnter from "@/ui/animations/AnimateOnEnter"
import Typography from "@/ui/atoms/Typography"
import Carousel from "@/ui/molecules/Carousel"
import CtaLinkList from "@/ui/molecules/CtaLinkList"
import ListItemBlock from "@/ui/molecules/ListItemBlock"
import PictureFrame from "@/ui/molecules/PictureFrame"
import BlockContainer, {
  BlockContainerType,
} from "@/ui/structure/BlockContainer"
import { Grid, ResponsiveColumns, ResponsiveGap } from "@/ui/structure/Grid"
import { CtaData } from "@/ui/types/cta"
import { LinkData } from "@/ui/types/link"
import { ShapeRounding } from "@/ui/types/rounding"
import { getRounding } from "@/ui/utils/roundings"
import { classNames, ResponsiveStringValue } from "@punks/ui-core"
import { Fragment, ReactNode } from "react"

export type ItemsListBlockItem = {
  title?: ReactNode
  subtitle?: ReactNode
  caption?: ReactNode
  image?: ReactNode
  decoration?: ReactNode
  rounding?: ShapeRounding
  link?: LinkData
}

export type CtaItems = {
  items: CtaData[]
  direction?: "row" | "column"
}

export type ItemsListType = "carousel" | "grid"

export interface ItemsListBlockProps {
  title?: ReactNode
  items?: ItemsListBlockItem[]
  color?: "light" | "dark"
  alignment?: "center" | "left" | "centerXs"
  itemContainer?: "none" | "frame"
  container?: BlockContainerType
  gridLayout: {
    columns: ResponsiveColumns
    gap?: ResponsiveGap
  }
  cta?: CtaItems
  textPadding?: ResponsiveStringValue
  imageTopMargin?: ResponsiveStringValue
  listType: ItemsListType
  decorations?: ReactNode[]
}

const ItemsListBlock = ({
  title,
  items,
  alignment = "left",
  color = "light",
  container,
  gridLayout,
  cta,
  textPadding,
  imageTopMargin,
  listType = "grid",
  decorations,
  itemContainer = "none",
}: ItemsListBlockProps) => {
  return (
    <div
      className={classNames("relative py-8 xl:py-24 text-center xl:text-left", {
        "text-negative": color === "dark",
      })}
    >
      {color === "dark" && (
        <div className="absolute inset-0 z-[-2] bg-negative-contrast"></div>
      )}

      {decorations?.map((x, i) => (
        <AnimateOnEnter delay={250} animationType="fadeIn" key={i}>
          <div className="absolute inset-0">{x}</div>
        </AnimateOnEnter>
      ))}

      <BlockContainer
        noGuttersLg={container === "card" || listType === "carousel"}
        fluidLg={listType === "carousel"}
        type={container}
        classes={{
          card: classNames("pt-16 pb-8 xl:px-16 xl:-mx-10", {
            "bg-background text-text": color === "dark",
          }),
        }}
      >
        {alignment === "left" && (
          <Typography variant="subtitle2" weight="bold" className="mb-12">
            {title}
          </Typography>
        )}

        {alignment === "centerXs" && (
          <Typography
            variant="subtitle2"
            weight="bold"
            className="mb-12 !text-center xl:!text-left"
          >
            {title}
          </Typography>
        )}

        {alignment === "center" && (
          <Typography
            variant={["h5", "h2"]}
            weight="semiBold"
            className="mb-16 mt-8"
            textAlign={"center"}
          >
            {title}
          </Typography>
        )}

        {listType === "carousel" && (
          <Carousel
            slides={
              items?.map((item, index) => ({
                content: (
                  <div
                    key={index}
                    style={{ ...getRounding(item.rounding).styles }}
                    className={classNames({
                      "text-center pb-4 xl:pb-16": alignment === "center",
                    })}
                  >
                    <AppLink href={item.link?.url}>
                      <ListItemBlock
                        classes={{
                          root: classNames({
                            "p-4 pb-8": itemContainer === "frame",
                          }),
                          image: classNames(
                            "overflow-hidden",
                            getRounding(item.rounding).classes,
                            {
                              "rounded-frame shadow-picture":
                                itemContainer === "frame",
                            }
                          ),
                        }}
                        caption={item.caption}
                        image={item.image}
                        title={item.title}
                        subtitle={item.subtitle}
                        decoration={item.decoration}
                        textPadding={textPadding}
                        imageMartingTop={imageTopMargin}
                        centerXs={alignment === "centerXs"}
                      />
                    </AppLink>
                  </div>
                ),
              })) ?? []
            }
            defaultSettings={{
              slidesPerView: 1.4,
              slidesOffsetBefore: 24,
              slidesOffsetAfter: 24,
            }}
            responsiveSettings={{
              sm: {
                slidesPerView: 1.8,
                slidesOffsetBefore: 16,
                slidesOffsetAfter: 16,
              },
              lg: {
                slidesPerView: 3,
                slidesOffsetBefore: 0,
                slidesOffsetAfter: 0,
              },
            }}
            bars={"bars"}
          />
        )}

        {listType === "grid" && (
          <Grid cols={gridLayout.columns} gap={gridLayout.gap}>
            {items?.map((item, index) => (
              <div
                key={index}
                style={{ ...getRounding(item.rounding).styles }}
                className={classNames({
                  "text-center": alignment === "center",
                })}
              >
                <AppLink href={item.link?.url}>
                  <ListItemBlock
                    classes={{
                      image: classNames(
                        "overflow-hidden",
                        getRounding(item.rounding).classes
                      ),
                    }}
                    caption={item.caption}
                    image={item.image}
                    title={item.title}
                    subtitle={item.subtitle}
                    decoration={item.decoration}
                    textPadding={textPadding}
                    imageMartingTop={imageTopMargin}
                  />
                </AppLink>
              </div>
            ))}
          </Grid>
        )}

        {cta && (
          <CtaLinkList
            className="mt-12"
            items={cta.items}
            direction={cta.direction}
            align={"center"}
          />
        )}
      </BlockContainer>
    </div>
  )
}

export default ItemsListBlock
